import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow, format, addMinutes } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import {
  Mic,
  Upload,
  Clock,
  Disc,
  Signal,
  AlertTriangle,
  Maximize2,
  X,
  Radio,
  MapPin,
  User,
  Activity,
  HardDrive,
  Wifi,
  WifiOff,
} from 'lucide-react';

interface Recording {
  id: string;
  title: string;
  status: string;
  venueId?: string;
  venueName?: string;
  venue_name?: string;
  deviceId?: string;
  deviceName?: string;
  device_name?: string;
  artistId?: string;
  artistName?: string;
  artist_name?: string;
  startTime?: string;
  start_time?: string;
  captureMinutes?: number;
  capture_minutes?: number;
  metadata?: {
    capturedMinutes?: number;
    bufferedMinutes?: number;
    uploadProgress?: number;
    bytesUploaded?: number;
    totalBytes?: number;
    error?: string;
  };
}

interface LiveRecordingMonitorProps {
  recordings: Recording[];
  onClose?: () => void;
  isWebSocketConnected?: boolean;
}

const LiveRecordingMonitor: React.FC<LiveRecordingMonitorProps> = ({
  recordings,
  onClose,
  isWebSocketConnected = true,
}) => {
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second for live elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getElapsedMinutes = (recording: Recording): number => {
    const startTime = recording.startTime || recording.start_time;
    if (!startTime) return 0;

    const elapsed = (currentTime - new Date(startTime).getTime()) / 1000 / 60;
    return Math.max(0, elapsed);
  };

  const getTotalMinutes = (recording: Recording): number => {
    return recording.captureMinutes || recording.capture_minutes || 60;
  };

  const getProgress = (recording: Recording): number => {
    const elapsed = getElapsedMinutes(recording);
    const total = getTotalMinutes(recording);
    return Math.min(100, (elapsed / total) * 100);
  };

  const isOverrun = (recording: Recording): boolean => {
    const elapsed = getElapsedMinutes(recording);
    const total = getTotalMinutes(recording);
    return elapsed > total;
  };

  const getOverrunMinutes = (recording: Recording): number => {
    const elapsed = getElapsedMinutes(recording);
    const total = getTotalMinutes(recording);
    return Math.max(0, elapsed - total);
  };

  const getEstimatedEndTime = (recording: Recording): Date | null => {
    const startTime = recording.startTime || recording.start_time;
    if (!startTime) return null;

    const totalMinutes = getTotalMinutes(recording);
    return addMinutes(new Date(startTime), totalMinutes);
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          icon: Clock,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-500/10',
          borderColor: 'border-yellow-500/20',
          label: 'Pending',
          pulse: false,
        };
      case 'queued':
        return {
          icon: Signal,
          color: 'text-blue-600',
          bgColor: 'bg-blue-500/10',
          borderColor: 'border-blue-500/20',
          label: 'Queued',
          pulse: true,
        };
      case 'recording':
        return {
          icon: Mic,
          color: 'text-red-600',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/20',
          label: 'Recording',
          pulse: true,
        };
      case 'uploading':
        return {
          icon: Upload,
          color: 'text-orange-600',
          bgColor: 'bg-orange-500/10',
          borderColor: 'border-orange-500/20',
          label: 'Uploading',
          pulse: true,
        };
      default:
        return {
          icon: Activity,
          color: 'text-gray-600',
          bgColor: 'bg-gray-500/10',
          borderColor: 'border-gray-500/20',
          label: status,
          pulse: false,
        };
    }
  };

  const formatElapsedTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.floor((minutes % 1) * 60);

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  if (recordings.length === 0) {
    return null;
  }

  return (
    <>
      {/* Compact recording cards */}
      <div className="space-y-3">
        {recordings.map((recording, index) => {
          const statusInfo = getStatusInfo(recording.status);
          const StatusIcon = statusInfo.icon;
          const elapsedMinutes = getElapsedMinutes(recording);
          const totalMinutes = getTotalMinutes(recording);
          const progress = getProgress(recording);
          const estimatedEnd = getEstimatedEndTime(recording);

          return (
            <motion.div
              key={recording.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ delay: index * 0.1 }}
              className={`relative overflow-hidden rounded-lg border-2 ${statusInfo.borderColor} ${statusInfo.bgColor} backdrop-blur-sm`}
            >
              {/* Animated gradient background */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                animate={{
                  x: ['-100%', '200%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              />

              <div className="relative p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <motion.div
                        animate={statusInfo.pulse ? {
                          scale: [1, 1.2, 1],
                          opacity: [1, 0.6, 1],
                        } : {}}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                      </motion.div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {recording.title}
                      </h4>
                    </div>

                    {/* Metadata */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                      {(recording.venueName || recording.venue_name) && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          <span>{recording.venueName || recording.venue_name}</span>
                        </div>
                      )}
                      {(recording.artistName || recording.artist_name) && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{recording.artistName || recording.artist_name}</span>
                        </div>
                      )}
                      {(recording.deviceName || recording.device_name) && (
                        <div className="flex items-center gap-1">
                          <Disc className="h-3 w-3" />
                          <span>{recording.deviceName || recording.device_name}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!isWebSocketConnected && (
                      <div className="relative group">
                        <WifiOff className="h-4 w-4 text-red-500" />
                        <span className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          Disconnected
                        </span>
                      </div>
                    )}
                    <Badge variant={recording.status === 'failed' ? 'destructive' : 'default'}>
                      {statusInfo.label}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setSelectedRecording(recording)}
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Progress for recording status */}
                {recording.status === 'recording' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Radio className="h-4 w-4 text-red-600 animate-pulse" />
                        <span className="font-mono font-medium">
                          {formatElapsedTime(elapsedMinutes)}
                        </span>
                        <span className="text-gray-500">
                          / {Math.floor(totalMinutes)} min
                        </span>
                      </div>
                      <div className="text-gray-600">
                        {Math.round(progress)}%
                      </div>
                    </div>

                    <div className="relative">
                      <Progress value={progress} className="h-2" />
                      {/* Animated pulse overlay */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-red-400/30 to-transparent rounded-full"
                        animate={{
                          x: ['-100%', '200%'],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'linear',
                        }}
                      />
                    </div>

                    {estimatedEnd && (
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>
                          Estimated completion: {format(estimatedEnd, 'HH:mm:ss')}
                        </span>
                        <span>
                          {Math.max(0, Math.floor(totalMinutes - elapsedMinutes))} min remaining
                        </span>
                      </div>
                    )}

                    {/* Device metadata from WebSocket */}
                    {recording.metadata?.capturedMinutes !== undefined && (
                      <div className="flex items-center gap-4 text-xs text-gray-600 pt-1 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-1">
                          <HardDrive className="h-3 w-3" />
                          <span>Captured: {recording.metadata.capturedMinutes} min</span>
                        </div>
                        {recording.metadata.bufferedMinutes !== undefined && (
                          <div className="flex items-center gap-1">
                            <Activity className="h-3 w-3" />
                            <span>Buffer: {recording.metadata.bufferedMinutes} min</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Progress for uploading status */}
                {recording.status === 'uploading' && (
                  <div className="space-y-2">
                    {recording.metadata?.uploadProgress !== undefined ? (
                      <>
                        <div className="flex items-center justify-between text-sm">
                          <span>Uploading to cloud...</span>
                          <span className="font-medium">
                            {Math.round(recording.metadata.uploadProgress)}%
                          </span>
                        </div>
                        <Progress value={recording.metadata.uploadProgress} className="h-2" />
                        {recording.metadata.bytesUploaded !== undefined && recording.metadata.totalBytes !== undefined && (
                          <div className="text-xs text-gray-600">
                            {formatFileSize(recording.metadata.bytesUploaded)} / {formatFileSize(recording.metadata.totalBytes)}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Upload className="h-4 w-4 animate-bounce" />
                        <span>Uploading...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Pending/Queued status */}
                {(recording.status === 'pending' || recording.status === 'queued') && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Signal className="h-4 w-4 animate-pulse" />
                    <span>
                      {recording.status === 'pending'
                        ? 'Waiting to start...'
                        : 'Sent to device, awaiting acknowledgment...'}
                    </span>
                  </div>
                )}

                {/* Error */}
                {recording.status === 'failed' && recording.metadata?.error && (
                  <div className="flex items-center gap-2 p-2 bg-red-100 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{recording.metadata.error}</span>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Detailed modal */}
      <AnimatePresence>
        {selectedRecording && (
          <Dialog open={true} onOpenChange={() => setSelectedRecording(null)}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const statusInfo = getStatusInfo(selectedRecording.status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <>
                        <motion.div
                          animate={statusInfo.pulse ? {
                            scale: [1, 1.2, 1],
                            opacity: [1, 0.6, 1],
                          } : {}}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                          }}
                        >
                          <StatusIcon className={`h-6 w-6 ${statusInfo.color}`} />
                        </motion.div>
                        {selectedRecording.title}
                      </>
                    );
                  })()}
                </DialogTitle>
                <DialogDescription>
                  Live recording monitoring - Real-time status and progress
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Status badge */}
                <div className="flex items-center justify-between">
                  <Badge variant={selectedRecording.status === 'failed' ? 'destructive' : 'default'} className="text-lg px-4 py-1">
                    {getStatusInfo(selectedRecording.status).label}
                  </Badge>
                  {!isWebSocketConnected && (
                    <div className="flex items-center gap-2 text-sm text-red-600">
                      <WifiOff className="h-4 w-4" />
                      <span>WebSocket Disconnected</span>
                    </div>
                  )}
                </div>

                {/* Recording info grid */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  {(selectedRecording.venueName || selectedRecording.venue_name) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Venue</div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">{selectedRecording.venueName || selectedRecording.venue_name}</span>
                      </div>
                    </div>
                  )}
                  {(selectedRecording.artistName || selectedRecording.artist_name) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Artist</div>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">{selectedRecording.artistName || selectedRecording.artist_name}</span>
                      </div>
                    </div>
                  )}
                  {(selectedRecording.deviceName || selectedRecording.device_name) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Device</div>
                      <div className="flex items-center gap-2">
                        <Disc className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">{selectedRecording.deviceName || selectedRecording.device_name}</span>
                      </div>
                    </div>
                  )}
                  {(selectedRecording.startTime || selectedRecording.start_time) && (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Started</div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">
                          {format(new Date(selectedRecording.startTime || selectedRecording.start_time || ''), 'HH:mm:ss')}
                          <span className="text-xs text-gray-500 ml-2">
                            ({formatDistanceToNow(new Date(selectedRecording.startTime || selectedRecording.start_time || ''), { addSuffix: true })})
                          </span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Detailed progress for recording */}
                {selectedRecording.status === 'recording' && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-3">Recording Progress</h4>
                      <div className="space-y-3">
                        {/* Elapsed time */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Elapsed Time</span>
                          <span className="font-mono text-2xl font-bold">
                            {formatElapsedTime(getElapsedMinutes(selectedRecording))}
                          </span>
                        </div>

                        {/* Progress bar */}
                        <div>
                          <div className="flex items-center justify-between mb-2 text-sm">
                            <span>Progress</span>
                            <span>{Math.round(getProgress(selectedRecording))}%</span>
                          </div>
                          <div className="relative">
                            <Progress value={getProgress(selectedRecording)} className="h-3" />
                            <motion.div
                              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full"
                              animate={{
                                x: ['-100%', '200%'],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'linear',
                              }}
                            />
                          </div>
                        </div>

                        {/* Time remaining */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Time Remaining</span>
                          <span className="font-medium">
                            {Math.max(0, Math.floor(getTotalMinutes(selectedRecording) - getElapsedMinutes(selectedRecording)))} minutes
                          </span>
                        </div>

                        {/* Estimated completion */}
                        {getEstimatedEndTime(selectedRecording) && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Estimated Completion</span>
                            <span className="font-medium">
                              {format(getEstimatedEndTime(selectedRecording)!, 'HH:mm:ss')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Device metrics (if available from WebSocket) */}
                    {(selectedRecording.metadata?.capturedMinutes !== undefined || selectedRecording.metadata?.bufferedMinutes !== undefined) && (
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <h4 className="font-semibold mb-2 text-sm">Device Metrics</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {selectedRecording.metadata?.capturedMinutes !== undefined && (
                            <div>
                              <span className="text-gray-600">Captured:</span>
                              <span className="ml-2 font-medium">{selectedRecording.metadata.capturedMinutes} min</span>
                            </div>
                          )}
                          {selectedRecording.metadata?.bufferedMinutes !== undefined && (
                            <div>
                              <span className="text-gray-600">Buffered:</span>
                              <span className="ml-2 font-medium">{selectedRecording.metadata.bufferedMinutes} min</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Connection status */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded text-sm">
                  {isWebSocketConnected ? (
                    <>
                      <Wifi className="h-4 w-4 text-green-600" />
                      <span className="text-green-600">Connected - Receiving real-time updates</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="h-4 w-4 text-red-600" />
                      <span className="text-red-600">Disconnected - Updates may be delayed</span>
                    </>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setSelectedRecording(null)}>
                    Close
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>
    </>
  );
};

export default LiveRecordingMonitor;
